import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import {
    noticeError,
    useModal,
    trackHeapEvent,
    getReferrerURL,
    getAffiliateData,
    AFFILIATE_QUERY_PARAM_KEYS,
    LightboxFullScreenTakeoverStyles,
} from '@ratehub/base-ui';

import GIFT_CARD_LEAD_QUERY_PARAMS from '../definitions/GiftCardLeadQueryParams';
import trackHeapCCWebClickEvent from '../functions/trackHeapCCWebClickEvent';
import trackHeapCCRedirectEvent from '../functions/trackHeapCCRedirectEvent';
import navigateNewTabToMoreDetails from '../functions/navigateNewTabToMoreDetails';
import CreditCardIncentive from '../components/CreditCardIncentive';
import isCardEligibleForReferralOffer from '../functions/isCardEligibleForReferralOffer';


/**
 * Creates a callback for applying for a credit card.
 * When the card features a gift card offer it will open the gift card modal before proceeding with normal processing.
 *
 * @returns {(function({
 *      cardInfo: {CreditCardSynopsisShape},
 *      [onApplyNow: onApplyNowCallback],
 *      [applyURLOverride: string],
 *      [provinceCode: string],
 *      [enableRedirect: boolean],
 *  })
 *      : (null|undefined))|*}
 */
function useApplyForCreditCardCallback() {
    const modal = useModal();

    return useCallback(
        /**
         * To be invoked when a user is applying for a credit card so that it will open the gift card modal when needed.
         *
         * @param {CreditCardSynopsisShape} cardInfo
         * @param {GiftOfferShape} [cardInfo.giftCardOffer]
         * @param {onApplyNowCallback} [onApplyNow]
         * @param {string} [applyURLOverride]
         * @param {string} [provinceCode]
         * @param {boolean} [enableRedirect]
         * @returns {null}
         */
        ({
            cardInfo,
            onApplyNow,
            applyURLOverride,
            provinceCode,
            enableRedirect = true,
        }) => {
            if (!cardInfo) {
                noticeError(new RangeError('`cardInfo` argument to credit card callback must not be empty.'));

                return null;
            }

            trackHeapCCWebClickEvent(cardInfo);

            const {
                isMonetized,
                applyRedirectURL,
                detailsURL,
                isSponsored,
                bank,
                giftCardOffer,
                hasReferralOffer,
            } = cardInfo;


            const hasGiftCardOffer = !!giftCardOffer;
            const isReferralEligible = isCardEligibleForReferralOffer({ hasGiftCardOffer, hasReferralOffer });

            // do the standard onApplyNow() action when there is no gift card offer and no referral eligibility
            if (!hasGiftCardOffer && !isReferralEligible) {
                trackHeapEvent('CC conversion attempt', {
                    href: detailsURL,
                    provider: bank,
                    monetized: isMonetized.toString(),
                    sponsored: isSponsored.toString(),
                });

                trackHeapCCRedirectEvent({
                    referrerURL: getReferrerURL(),
                    affiliateId: getAffiliateData()?.[AFFILIATE_QUERY_PARAM_KEYS.affiliateId],
                    cardInfo,
                });

                onApplyNow?.(cardInfo);
                if (enableRedirect) {
                    navigateNewTabToMoreDetails({
                        isMonetized,
                        detailsURL,
                        applyRedirectURL,
                        applyRedirectURLOverride: applyURLOverride,
                        queryParams: {
                            ...(provinceCode && { province: provinceCode }),
                            sponsored: isSponsored,
                        },
                    });
                }

                return null;
            }

            // if we did not exit early with the return directly above:
            //  • there IS a gift card offer OR a refferal offer, AND
            //  • we have been asked to show it (by context or by experiment)
            // If this is invoked, we know they explicitly accepted or declined the offer.
            // They did NOT cancel, and we are about to send them to the provider’s site.
            function handleAcceptOrDecline(additionalInfo) {
                modal.close(modalId);

                const { email, firstName, lastName, isOfferAccepted } = additionalInfo;

                trackHeapCCRedirectEvent({
                    referrerURL: getReferrerURL(),
                    affiliateId: getAffiliateData()?.[AFFILIATE_QUERY_PARAM_KEYS.affiliateId],
                    additionalInfo,
                    cardInfo,
                });

                onApplyNow?.(cardInfo);
                navigateNewTabToMoreDetails({
                    isMonetized,
                    detailsURL,
                    applyRedirectURL,
                    applyRedirectURLOverride: applyURLOverride,
                    queryParams: {
                        ...(provinceCode && { province: provinceCode }),
                        ...(isOfferAccepted && {
                            [GIFT_CARD_LEAD_QUERY_PARAMS.email]: email,
                            [GIFT_CARD_LEAD_QUERY_PARAMS.firstName]: firstName,
                            // make sure to send an empty string to confirm intentional blankness
                            [GIFT_CARD_LEAD_QUERY_PARAMS.lastName]: lastName ?? '',
                            [GIFT_CARD_LEAD_QUERY_PARAMS.isGiftCardLead]: isOfferAccepted,
                        }),
                        sponsored: isSponsored,
                    },
                });
            }

            // We use the modal’s layer id to close the modal programmatically
            const modalId = modal.open({
                isContentFullWidth: true,
                showBranding: false,
                verticalAlignment: 'flex-start',

                style: LightboxFullScreenTakeoverStyles,

                // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
                screenReaderLabel: hasGiftCardOffer
                    ? (
                        <FormattedMessage
                            id="edb.cc.gift-card.screen-reader"
                            defaultMessage="Credit card gift offer"
                        />
                    )
                    : (
                        <FormattedMessage
                            id="edb.cc.referral-offer.screen-reader"
                            defaultMessage="Referral offer bonus"
                        />
                    ),

                children: (
                    <CreditCardIncentive
                        cardInfo={cardInfo}
                        onAcceptOrDecline={handleAcceptOrDecline}
                        isReferralEligible={isReferralEligible}
                    />
                ),
            });
        },
        [ modal ],
    );
}


/**
 * @callback onApplyNowCallback
 * @param {CreditCardSynopsisShape} cardInfo
 */

export default useApplyForCreditCardCallback;
